import { ChevronDown } from "lucide-react";
import { useImmer } from "use-immer";

import {
  IgBorderColors,
  IgCoreBackgroundColors,
} from "@intelligems/ig-design-tokens";
import { IgTypography } from "@intelligems/ig-theme";
import { SurveySummaryRow } from "@intelligems/ig-types";
import Stack from "@mui/material/Stack";
import ButtonBase from "@mui/material/ButtonBase";
import { useTheme } from "@mui/material";

/**
 * The Figma "Survey summary" (Atlas Component Library › node 222-23): how an
 * answers reply reads back in the transcript. A survey is answered once but
 * covers several questions, so the reply used to land as one run-on user
 * bubble; this is the recap card that replaces it — one "Survey summary row"
 * per question, the question in semibold over the chosen answer.
 *
 * A row whose answer collapsed to a count (`items` present, e.g. "4 selected")
 * gets a chevron and expands to the itemized picks. The rows themselves are
 * built in `buildSurveySummary` so the optimistic bubble and the server echo
 * that reconciles over it render the identical card.
 */
export const SurveySummaryCard = ({ rows }: { rows: SurveySummaryRow[] }) => {
  return (
    <Stack
      data-testid="survey-summary"
      gap={4}
      sx={{
        backgroundColor: IgCoreBackgroundColors.baseLightHigh,
        border: `1px solid ${IgBorderColors.core.disabled}`,
        borderRadius: "4px",
        p: 4,
        width: "100%",
      }}
    >
      {rows.map((row, index) => (
        <SummaryRow key={`${row.prompt}-${index}`} row={row} />
      ))}
    </Stack>
  );
};

const SummaryRow = ({ row }: { row: SurveySummaryRow }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useImmer(false);
  const expandable = Boolean(row.items && row.items.length > 0);

  const answer = (
    <IgTypography variant="body2" sx={{ color: theme.palette.text.secondary }}>
      {row.answer}
    </IgTypography>
  );

  return (
    <Stack gap={1}>
      <IgTypography variant="body2" sx={{ fontWeight: 600 }}>
        {row.prompt}
      </IgTypography>
      {expandable ? (
        <ButtonBase
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
          sx={{
            alignSelf: "flex-start",
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}
        >
          {answer}
          <ChevronDown
            size={14}
            color={theme.palette.text.secondary}
            style={{
              transform: expanded ? "rotate(180deg)" : undefined,
              flexShrink: 0,
            }}
          />
        </ButtonBase>
      ) : (
        answer
      )}
      {expandable && expanded && (
        <Stack gap={1}>
          {row.items?.map((item, index) => (
            <IgTypography
              key={`${item}-${index}`}
              variant="body2"
              sx={{ color: theme.palette.text.tertiary }}
            >
              {item}
            </IgTypography>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
